import { gsap } from "gsap";
import { IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { GameFont } from "../../../../common/GameFont";
import { BasePageView } from "../../../../common/components/BasePageView";
import { ButtonWithScaleAnimation } from "../../../../common/components/ButtonWithScaleAnimation";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { FadeScreen } from "../../../../game/components/FadeScreen";
import { DicesBackground } from "../../components/DicesBackground";
import { RewardContainer } from "../../components/RewardContainer";
import { IDiceRollOfferController } from "../../controller/IDiceRollOfferController";
import { IDiceRollsOfferModel } from "../../model/IDiceRollsOfferModel";
import { Web3Model } from "../../../../../../web3/models/Web3Model";
import { ILogger } from "../../../../../logger/ILogger";
import { VirtualScreenEvent } from "by/gamefactory/analytics/events/VirtualScreenEvent";
import { StarsResourcePackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/StarsResourcePackage";
import { DiceRollOfferResourcePackage } from "../../resources/DiceRollOfferResourcePackage";
import { DicesResourcePackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/DicesResourcePackage";
import { IAnalyticProviderWrapper } from "../../../../../../analytics/IAnalyticProviderWrapper";
import { PaymentResult } from "by/gamefactory/web3/PaymentResult";
import { FullscreenInputBlocker } from "by/gamefactory/cityversetycoon/scenes/game/components/FullscreenInputBlocker";

export class DiceRollOfferPage extends BasePageView {
    private static readonly SPECIAL_OFFER_TEXT = "SPECIAL OFFER";
    private static readonly PAYMENT_TEXT = "*The prices above may incur additional payment charges";

    private readonly commonResources: CommonResourcesPackage;
    private readonly starsResources: StarsResourcePackage;
    private readonly dicesResources: DicesResourcePackage;
    private readonly diceRollsOfferResources: DiceRollOfferResourcePackage;
    private readonly diceRollsOfferModel: IDiceRollsOfferModel;
    private readonly diceRollsOfferController: IDiceRollOfferController;
    private readonly operationControls: PromiseCompletionSource<void>;
    private readonly web3Model: Web3Model;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private readonly logger: ILogger;

    private fadeScreen: FadeScreen;
    private greenLight: Sprite;
    private purpleLight: Sprite;
    private outOfDiceRollsImage: Sprite;
    private specialOfferText: Text;
    private rewardContainer: RewardContainer;
    private buyButton: ButtonWithScaleAnimation;
    private closeButton: ButtonWithScaleAnimation;
    private dicesBackground: DicesBackground;
    private paymentText: Text;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        commonResources: CommonResourcesPackage,
        starsResources: StarsResourcePackage,
        dicesResources: DicesResourcePackage,
        diceRollsOfferResources: DiceRollOfferResourcePackage,
        canvasScaler: ICanvasScaler,
        diceRollsOfferModel: IDiceRollsOfferModel,
        diceRollsOfferController: IDiceRollOfferController,
        web3Model: Web3Model,
        analyticsProvider: IAnalyticProviderWrapper,
        logger: ILogger
    ) {
        super(canvasScaler);
        this.operationControls = operationControls;
        this.commonResources = commonResources;
        this.starsResources = starsResources;
        this.dicesResources = dicesResources;
        this.diceRollsOfferResources = diceRollsOfferResources;
        this.diceRollsOfferModel = diceRollsOfferModel;
        this.diceRollsOfferController = diceRollsOfferController;
        this.web3Model = web3Model;
        this.analyticsProvider = analyticsProvider;
        this.logger = logger;
        this.build();
        this.once("added", this.show.bind(this));
    }

    public start(): void {}

    protected override build(): void {
        this.fadeScreen = new FadeScreen(0.5, 0, 0.9, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);

        this.greenLight = new Sprite(this.diceRollsOfferResources.assets_views_dice_roll_offer_light_green_png);
        this.greenLight.anchor.set(0.5, 1);
        this.addChild(this.greenLight);

        this.purpleLight = new Sprite(this.diceRollsOfferResources.assets_views_dice_roll_offer_light_purple_png);
        this.purpleLight.anchor.x = 0.5;
        this.addChild(this.purpleLight);

        this.dicesBackground = new DicesBackground(
            this.commonResources,
            this.starsResources,
            this.dicesResources,
            this.canvasScaler.referenceWidth,
            this.canvasScaler.referenceHeight
        );
        this.addChild(this.dicesBackground);

        this.outOfDiceRollsImage = new Sprite(this.diceRollsOfferResources.assets_views_dice_roll_offer_out_of_dice_rolls_png);
        this.outOfDiceRollsImage.anchor.set(0.5, 0.5);
        this.addChild(this.outOfDiceRollsImage);

        this.specialOfferText = new Text(DiceRollOfferPage.SPECIAL_OFFER_TEXT, new SpecialOfferTextStyle());
        this.specialOfferText.anchor.set(0.5, 0.5);
        this.specialOfferText.skew._x = -0.087;
        this.specialOfferText.angle = -5;
        this.addChild(this.specialOfferText);

        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.hitArea = new Rectangle(
            -this.closeButton.width * 0.5,
            -this.closeButton.height,
            this.closeButton.width * 2,
            this.closeButton.height * 2
        );
        this.closeButton.onUp.once(this.hidePage, this);
        this.addChild(this.closeButton);

        this.rewardContainer = new RewardContainer(
            this.diceRollsOfferResources.assets_views_dice_roll_offer_dice_result_png,
            this.diceRollsOfferModel.diceRollsOffer.value.reward.diceRolls
        );
        this.rewardContainer.scale.set(0.7);
        this.addChild(this.rewardContainer);

        this.buyButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_button_green_png);
        this.buyButton.onUp.add(this.buyButtonClickHandle, this);
        this.addChild(this.buyButton);

        const buttonText = new Text(
            `${this.diceRollsOfferModel.diceRollsOffer.value.price} ${this.diceRollsOfferModel.diceRollsOffer.value.currency}`,
            new ButtonTextStyle()
        );
        buttonText.position.set(this.buyButton.width * 0.5 - buttonText.width * 0.5, this.buyButton.height * 0.5 - buttonText.height * 0.5);
        this.buyButton.addChild(buttonText);

        this.paymentText = new Text(DiceRollOfferPage.PAYMENT_TEXT, new PaymentInfoTextStyle());
        this.addChild(this.paymentText);
        super.build();
    }

    private async show(): Promise<void> {
        this.fadeScreen.fadeIn();
    }

    private async buyButtonClickHandle(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        try {
            this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Click on buy in Run out of coins offer", this.analyticsProvider.currentEventIndex));
            const paymentResult = await this.diceRollsOfferController.showPaymentPopup(
                this.diceRollsOfferModel.diceRollsOffer.value.tokenId,
                this.diceRollsOfferModel.diceRollsOffer.value.price
            );
            this.logger.debug("Payment finished with result: " + paymentResult);
            if (paymentResult === PaymentResult.SUCCESS) {
                this.hidePage();
                await this.diceRollsOfferController.showMyWalletPopup();
            }
        } catch (error: unknown) {
            this.logger.error(error);
        } finally {
            this.removeChild(inputBlocker);
        }
    }

    private async hidePage(): Promise<void> {
        this.closeButton.eventMode = "none";
        const animationTimeLine = new gsap.core.Timeline();
        await animationTimeLine.to(this, { alpha: 0, duration: 0.5, ease: "power1.out" });
        animationTimeLine.kill();
        this.operationControls.complete();
    }

    protected override layout(width: number, height: number): void {
        this.greenLight.position.set(width * 0.5, height * 1);
        this.purpleLight.position.set(width * 0.5, 0);
        this.dicesBackground.position.set(width * 0.5, height * 0.5);
        this.outOfDiceRollsImage.position.set(width * 0.5, 483.76);
        this.specialOfferText.position.set(width * 0.5, height * 0.55);
        this.closeButton.position.set(55.74, 94.18);
        this.rewardContainer.position.set(width * 0.6, height * 0.676);
        this.buyButton.position.set(width * 0.5 - this.buyButton.width * 0.5, 1405.67);
        this.paymentText.position.set(width * 0.5 - this.paymentText.width * 0.5, 1690);
        super.layout(width, height);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.buyButton.onUp.remove(this.buyButtonClickHandle, this);
        super.destroy(options);
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(
            new TextStyle({
                fill: "#ffffff",
                align: "center",
                fontFamily: GameFont.DEFAULT_FONT,
                fontSize: 78,
                dropShadow: true,
                dropShadowAngle: 0.67,
                dropShadowBlur: 5,
                dropShadowColor: "#0D06064D",
                dropShadowDistance: 3,
                dropShadowAlpha: 0.4,
            })
        );
    }
}

class SpecialOfferTextStyle extends TextStyle {
    public constructor() {
        super(
            new TextStyle({
                fill: "#ffffff",
                align: "center",
                fontFamily: GameFont.DEFAULT_FONT,
                fontSize: 128,
            })
        );
    }
}

class PaymentInfoTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 36,
            letterSpacing: 1.25,
        });
    }
}
